import React from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  IconButton,
  Alert,
} from '@mui/material';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import { useInsuranceAddDialog } from './hooks';
import {
  BasicInfoSection,
  PolicyInfoSection,
  InsuredInfoSection,
  DialogActionButtons,
} from './components';

interface InsuranceAddDialogProps {
  onInsuranceAdded?: () => void;
}

const InsuranceAddDialog: React.FC<InsuranceAddDialogProps> = ({ onInsuranceAdded }) => {
  const {
    open,
    payers,
    isEncounterCancelled,
    handleOpen,
    handleClose,
    handleSubmit,
    formData,
    selectedPayer,
    sameAsPatient,
    error,
    handleInputChange,
    handlePayerChange,
    handleSameAsPatientChange,
  } = useInsuranceAddDialog({ onInsuranceAdded });

  // Don't render if encounter is cancelled
  if (isEncounterCancelled) {
    return null;
  }

  return (
    <>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={handleOpen}
        sx={{
          backgroundColor: '#162346',
          color: 'white',
          fontWeight: 600,
          px: 3,
          py: 1,
          '&:hover': {
            backgroundColor: '#1a2a52'
          }
        }}
      >
        Add Insurance
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
          p: 2.5
        }}>
          <Typography variant="h6" fontWeight={600} color="#1e293b">
            Add New Insurance
          </Typography>
          <IconButton
            onClick={handleClose}
            sx={{ color: '#64748b' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <Stack spacing={3} sx={{ p: 3 }}>
            <BasicInfoSection
              priority={formData.priority}
              payerCode={formData.payerCode}
              payerName={formData.payerName}
              payers={payers}
              selectedPayer={selectedPayer}
              onPayerChange={handlePayerChange}
            />

            <PolicyInfoSection
              policyNumber={formData.policyNumber}
              groupNumber={formData.groupNumber}
              groupName={formData.groupName}
              onInputChange={handleInputChange}
            />

            <InsuredInfoSection
              sameAsPatient={sameAsPatient}
              insuredsFirstName={formData.insuredsFirstName}
              insuredsMiddleName={formData.insuredsMiddleName}
              insuredsLastName={formData.insuredsLastName}
              insuredsSuffix={formData.insuredsSuffix}
              insuredsRelationship={formData.insuredsRelationship}
              insuredsDateOfBirth={formData.insuredsDateOfBirth}
              insuredsStreet1={formData.insuredsStreet1}
              insuredsStreet2={formData.insuredsStreet2}
              insuredsCity={formData.insuredsCity}
              insuredsState={formData.insuredsState}
              insuredsZip={formData.insuredsZip}
              onSameAsPatientChange={handleSameAsPatientChange}
              onInputChange={handleInputChange}
            />

            {/* Error Message Section */}
            {error && (
              <Alert severity="error">
                {error}
              </Alert>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <DialogActionButtons
            onCancel={handleClose}
            onSubmit={handleSubmit}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InsuranceAddDialog;
